import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { FileSpreadsheet, RefreshCw, Sparkles } from 'lucide-react'
import { Button, Select } from 'vnetwork-ui'
import { Control } from 'react-hook-form'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { DEPARTMENT_IDS } from '@/constants/departments'
import type { SurveyFilters } from '../../../hooks/use-survey-list'
import { getQuarterPeriod, QUARTERS } from '../../../utils/report-filters'

interface ReportFiltersProps {
    control: Control<SurveyFilters>
    onExport?: () => void
    onRefresh?: () => void
    loading?: boolean
    insightsLoading?: boolean
}

const QUARTER_OPTIONS = QUARTERS.map((q) => ({ label: q, value: q }))

const generateYearOptions = (selected: string) => {
    const currentYear = new Date().getFullYear()
    return [
        ...new Set([
            Number(selected),
            currentYear,
            currentYear - 1,
            currentYear - 2,
        ]),
    ]
        .sort((a, b) => b - a)
        .map((y) => ({
            label: y.toString(),
            value: y.toString(),
        }))
}

const quarterIndex = (q: string) =>
    QUARTER_OPTIONS.findIndex((o) => o.value === q)

export const ReportFilters = ({
    control,
    onExport,
    onRefresh,
    loading,
    insightsLoading,
}: ReportFiltersProps) => {
    const { t } = useTranslation('surveys')
    const [searchParams, setSearchParams] = useSearchParams()
    const {
        year,
        from: fromQuarter,
        to: toQuarter,
    } = getQuarterPeriod(searchParams)

    const setParam = (key: string, value: string) => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                next.set(key, value)
                next.set('page', '1')
                return next
            },
            { replace: true }
        )
    }

    const fromOptions = QUARTER_OPTIONS.filter(
        (_, i) => i <= quarterIndex(toQuarter)
    )
    const toOptions = QUARTER_OPTIONS.filter(
        (_, i) => i >= quarterIndex(fromQuarter)
    )

    const handleFromChange = (v: unknown) => {
        const val = String(v)
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                next.set('rpt_from', val)
                next.set('page', '1')
                if (quarterIndex(val) > quarterIndex(toQuarter))
                    next.set('rpt_to', val)
                return next
            },
            { replace: true }
        )
    }

    const handleToChange = (v: unknown) => {
        const val = String(v)
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                next.set('rpt_to', val)
                next.set('page', '1')
                if (quarterIndex(val) < quarterIndex(fromQuarter))
                    next.set('rpt_from', val)
                return next
            },
            { replace: true }
        )
    }

    return (
        <div className="w-full bg-white rounded-lg p-4 border border-gray-100 grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-3">
            <Select
                value={year}
                onChange={(v) => setParam('rpt_year', String(v))}
                options={generateYearOptions(year)}
                allowClear={false}
                label09={t('surveys.quarterly_report.year')}
            />

            <Select
                value={fromQuarter}
                onChange={handleFromChange}
                options={fromOptions}
                allowClear={false}
                label09={t('surveys.quarterly_report.from_quarter')}
            />

            <Select
                value={toQuarter}
                onChange={handleToChange}
                options={toOptions}
                allowClear={false}
                label09={t('surveys.quarterly_report.to_quarter')}
            />

            <EmployeeSelect
                control={control}
                name="employee_ids"
                multiple
                showLabel
                label={t('surveys.filter.surveyor')}
            />

            <EmployeeSelect
                control={control}
                name="customer_sale_followers"
                multiple
                showLabel
                label={t('surveys.filter.sale_follow')}
                departmentIds={[DEPARTMENT_IDS.SALES]}
            />

            <EmployeeSelect
                control={control}
                name="customer_cs_followers"
                multiple
                showLabel
                label={t('surveys.filter.cs_follow')}
                departmentIds={[DEPARTMENT_IDS.ACCOUNT]}
            />

            {(onRefresh || onExport) && (
                <div className="flex items-end gap-2">
                    {onRefresh && (
                        <Button
                            onClick={onRefresh}
                            disabled={loading}
                            variant="outlined"
                            startIcon={
                                <RefreshCw
                                    className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}
                                />
                            }
                        >
                            {t('surveys.quarterly_report.refresh')}
                        </Button>
                    )}
                    {onExport && (
                        <Button
                            onClick={onExport}
                            disabled={loading}
                            startIcon={
                                insightsLoading ? (
                                    <Sparkles className="w-3.5 h-3.5 animate-pulse text-violet-400" />
                                ) : (
                                    <FileSpreadsheet className="w-3.5 h-3.5" />
                                )
                            }
                            title={
                                insightsLoading
                                    ? t(
                                          'surveys.quarterly_report.insights_loading_tooltip'
                                      )
                                    : undefined
                            }
                        >
                            {t('surveys.quarterly_report.export_excel')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}
